import { useState } from "react";
import { X, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { TermsDialog } from "@/components/TermsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const FIELDS = [
  { name: "full_name", label: "Full Name", required: true },
  { name: "email", label: "Work Email", type: "email", required: true },
  { name: "phone", label: "Phone" },
  { name: "organisation", label: "Laboratory / Institution" },
  { name: "designation", label: "Designation" },
  { name: "city", label: "City" },
];

export const BetaEnrollDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({});
  const [termsOpen, setTermsOpen] = useState(false);
  const [readTerms, setReadTerms] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/beta-requests", { ...form, agree_terms: true });
      setDone(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const canSubmit = readTerms && agree && form.full_name && form.email &&
    (form.qualification || "").trim().length > 4;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      data-testid="beta-dialog">
      <div className="bg-white rounded-md w-full max-w-2xl my-6">
        <div className="flex items-start justify-between p-5 border-b border-[#E5E7EB]">
          <div>
            <h2 className="font-heading text-lg font-bold text-[#111827]">Enrol as a Beta User</h2>
            <p className="text-xs text-[#6B7280] mt-0.5">
              Tell us about yourself. PathLete<sup>®</sup> reviews every application before granting access.
            </p>
          </div>
          <button onClick={onClose} data-testid="beta-close-btn" className="p-1 text-[#6B7280] hover:text-[#111827]">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="beta-success">
            <CheckCircle2 className="h-10 w-10 text-[#2E7D32] mx-auto" />
            <h3 className="font-heading font-bold text-lg mt-4 text-[#111827]">Application received</h3>
            <p className="text-sm text-[#6B7280] mt-2">
              Our team will review your details and get in touch by email. For anything urgent, write to{" "}
              <a href="mailto:info@pathlete.org" className="text-[#002FA7] hover:underline">info@pathlete.org</a>.
            </p>
            <Button onClick={onClose} data-testid="beta-done-btn" className="mt-6 bg-[#002FA7] hover:bg-[#00237C] text-white">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 grid sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.name}>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">
                  {f.label}{f.required && <span className="text-[#C92A2A]"> *</span>}
                </Label>
                <Input type={f.type || "text"} value={form[f.name] || ""} onChange={set(f.name)}
                  required={f.required} data-testid={`beta-${f.name}`} className="mt-1.5" />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">
                Qualification <span className="text-[#C92A2A]">*</span>
              </Label>
              <Textarea rows={2} value={form.qualification || ""} onChange={set("qualification")}
                placeholder="e.g. MD Pathology, DNB Haematology, DMLT…" data-testid="beta-qualification" className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Experience (brief)</Label>
              <Textarea rows={3} value={form.experience || ""} onChange={set("experience")}
                placeholder="Years of experience, current role, type of lab, areas of interest…"
                data-testid="beta-experience" className="mt-1.5" />
            </div>

            <div className="sm:col-span-2 rounded-md bg-[#F3F4F6] p-3">
              <button type="button" onClick={() => setTermsOpen(true)} data-testid="beta-open-terms-btn"
                className="text-sm text-[#002FA7] underline underline-offset-2">
                Read the Terms &amp; Conditions
              </button>
              <label className="flex items-start gap-2 mt-3 text-sm cursor-pointer">
                <Checkbox checked={agree} disabled={!readTerms} onCheckedChange={(v) => setAgree(!!v)}
                  data-testid="beta-agree-checkbox" className="mt-0.5" />
                <span className={readTerms ? "text-[#111827]" : "text-[#9CA3AF]"}>
                  I have read and agree to the Terms &amp; Conditions of PathLete India Pvt. Ltd.
                  {!readTerms && " (open and scroll through the terms to enable this)"}
                </span>
              </label>
            </div>

            {error && <p className="sm:col-span-2 text-sm text-[#C92A2A]" data-testid="beta-error">{error}</p>}

            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} data-testid="beta-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={!canSubmit || busy} data-testid="beta-submit-btn"
                className="bg-[#002FA7] hover:bg-[#00237C] text-white disabled:opacity-40">
                {busy ? "Submitting…" : "Submit application"}
              </Button>
            </div>
          </form>
        )}
      </div>

      <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} onAgreeReached={() => setReadTerms(true)} />
    </div>
  );
};
